import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, type Bench, type Campaign, type Project, type Run } from "../api";
import { monogram, productHue, useProduct } from "../product";
import { DOT, INK } from "./Look";
import { productState, type ProductState } from "./productState";

/**
 * Everything the header shows about products, fetched once and refreshed
 * every minute: the switcher's cards and the search box read the same lists.
 */
export function useProducts() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [benches, setBenches] = useState<Bench[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [runs, setRuns] = useState<Run[]>([]);
  const pull = useCallback(async () => {
    // Each list on its own: a service without campaigns, or a reader who may
    // not list runs, still gets a switcher with what it could read.
    await Promise.all([
      api.projects().then((d) => setProjects(d.projects)).catch(() => {}),
      api.benches().then((d) => setBenches(d.benches)).catch(() => {}),
      api.campaigns({ limit: 50 }).then((d) => setCampaigns(d.campaigns)).catch(() => {}),
      api.runs(undefined, 50).then((d) => setRuns(d.runs)).catch(() => {}),
    ]);
  }, []);
  useEffect(() => {
    void pull();
    const t = setInterval(() => void pull(), 60_000);
    return () => clearInterval(t);
  }, [pull]);
  const states = useMemo(
    () =>
      // Products with nothing in them (no bench, no run) go last: a "Test"
      // project someone made once should not sit between two real ones.
      projects
        .map((p) => productState(p, benches, campaigns, runs))
        .sort((a, b) => Number(b.total > 0 || b.project.run_count > 0) - Number(a.total > 0 || a.project.run_count > 0)),
    [projects, benches, campaigns, runs],
  );
  return { projects, benches, runs, states };
}

export function Tile({ id, name, size = "sm" }: { id: number; name: string; size?: "sm" | "lg" }) {
  return (
    <span
      aria-hidden="true"
      style={{ background: productHue(id, name) }}
      className={`inline-grid shrink-0 place-items-center font-bold text-white ${
        size === "lg" ? "h-9 w-9 rounded-[10px] text-xs" : "h-6 w-6 rounded-md text-xs"
      }`}
    >
      {monogram(name)}
    </span>
  );
}

/** Close on Escape and on a click outside `box` - what people try. */
function useDismiss(open: boolean, close: () => void, box: React.RefObject<HTMLElement>) {
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) close();
    };
    const key = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open, close, box]);
}

/**
 * The product switcher: the current product as one button, and a panel of
 * cards - one per product, each with the sentence its Overview would say -
 * rather than a list of names. Choosing where to go and seeing where something
 * is failing are the same glance.
 */
export function ProductSwitcher({ states }: { states: ProductState[] }) {
  const { t } = useTranslation();
  const { id } = useProduct();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, box);
  const navigate = useNavigate();
  const cur = states.find((s) => s.project.id === id) ?? null;

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-lg bg-white/10 py-1 pl-1 pr-2.5 text-sm font-semibold text-white transition-colors hover:bg-white/15"
        data-testid="product-switcher"
      >
        {cur ? <Tile id={cur.project.id} name={cur.project.name} /> : <span className="h-6 w-1" />}
        <span className="max-w-[10rem] truncate">{cur ? cur.project.name : t("product.choose")}</span>
        {cur && <span aria-hidden="true" className={`h-2 w-2 rounded-full ring-2 ring-white/80 ${DOT[cur.tone]}`} />}
        <span aria-hidden="true" className="text-xs text-sky-200">▾</span>
      </button>
      {open && (
        <div
          role="dialog"
          aria-label={t("product.panel")}
          className="absolute left-0 z-30 mt-2 w-[min(40rem,calc(100vw-2rem))] rounded-2xl border border-slate-200 bg-white p-4 text-slate-900 shadow-2xl dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        >
          <div className="mb-3 flex items-baseline justify-between gap-3">
            <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">{t("product.panel")}</span>
            <Link to="/now" onClick={close} className="text-sm text-brand-700 underline dark:text-sky-300">
              {t("product.all")}
            </Link>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {states.filter((s) => !isEmpty(s)).map((s) => (
              <button
                key={s.project.id}
                type="button"
                aria-current={s.project.id === id ? "true" : undefined}
                onClick={() => {
                  close();
                  navigate(`/projects/${s.project.id}`);
                }}
                className={`grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-0.5 rounded-xl border p-3 text-left transition hover:-translate-y-px hover:border-brand-400 motion-reduce:transition-none motion-reduce:hover:translate-y-0 ${
                  s.project.id === id
                    ? "border-brand-400 bg-brand-50 dark:bg-brand-900/40"
                    : "border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50"
                }`}
              >
                <span className="row-span-3"><Tile id={s.project.id} name={s.project.name} size="lg" /></span>
                <span className="font-semibold">{s.project.name}</span>
                <span className={`flex items-baseline gap-1.5 text-sm ${INK[s.tone]}`}>
                  <span aria-hidden="true" className={`inline-block h-2 w-2 shrink-0 self-center rounded-full ${DOT[s.tone]}`} />
                  {s.answer}
                </span>
                <span className="text-xs text-slate-500">{s.benches}</span>
              </button>
            ))}
          </div>
          {/* A project with no bench and no run is a name, not a product
              anyone is choosing between: one line, not two of six cards. */}
          {states.some(isEmpty) && (
            <p className="mt-3 text-sm text-slate-500">
              {t("product.empty")}{" "}
              {states.filter(isEmpty).map((s, i) => (
                <span key={s.project.id}>
                  {i > 0 && ", "}
                  <Link to={`/projects/${s.project.id}`} onClick={close} className="underline">{s.project.name}</Link>
                </span>
              ))}
            </p>
          )}
          <div className="mt-3 flex justify-between gap-3 text-xs text-slate-500">
            <Link to="/projects" onClick={close} className="underline">{t("product.list")}</Link>
            <span>{t("product.hint")}</span>
          </div>
        </div>
      )}
    </div>
  );
}

const isEmpty = (s: ProductState) => s.total === 0 && s.project.run_count === 0;

/** Where a product's tab points today, before its own pages exist. */
export function productTabs(id: number): { key: TabKey; to: string; match: (path: string, search: string) => boolean }[] {
  return [
    { key: "product.tabOverview", to: `/projects/${id}`, match: (p) => p === `/projects/${id}` },
    { key: "product.tabBuilds", to: `/projects/${id}/builds`, match: (p) => p.startsWith(`/projects/${id}/builds`) || p.startsWith("/campaigns") || p.startsWith("/runs/") },
    { key: "product.tabTests", to: `/projects/${id}/scenarios`, match: (p) => p.startsWith(`/projects/${id}/scenarios`) || p.startsWith(`/projects/${id}/cases/`) },
    { key: "product.tabBenches", to: `/benches?project=${id}`, match: (p) => p.startsWith("/benches") },
    { key: "product.tabSettings", to: `/projects/${id}/settings`, match: (p) => p === `/projects/${id}/settings` },
  ];
}
export type TabKey = "product.tabOverview" | "product.tabBuilds" | "product.tabTests" | "product.tabBenches" | "product.tabSettings";

export function ProductTabs({ className = "" }: { className?: string }) {
  const { t } = useTranslation();
  const { id } = useProduct();
  const { pathname, search } = useLocation();
  if (!id) return null;
  return (
    <nav className={`flex flex-wrap gap-1 ${className}`} aria-label={t("product.tabs")}>
      {productTabs(id).map((tab) => {
        const on = tab.match(pathname, search);
        return (
          <Link
            key={tab.key}
            to={tab.to}
            aria-current={on ? "page" : undefined}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              on ? "bg-white text-brand-700" : "text-sky-100 hover:bg-brand-600 dark:hover:bg-brand-800"
            }`}
          >
            {t(tab.key)}
          </Link>
        );
      })}
    </nav>
  );
}

type Hit = { kind: "product" | "page" | "bench" | "run" | "build" | "case"; label: string; to: string };

/**
 * Builds and cases of every product, fetched once, the first time search is
 * opened (K4b): "atstop" should find the case and "7.2.72" the build, as the
 * approved demo did. One request per product for each; a product that cannot
 * be read simply adds nothing.
 */
function useDeepHits(open: boolean, projects: Project[]): Hit[] {
  const [hits, setHits] = useState<Hit[] | null>(null);
  useEffect(() => {
    if (!open || hits !== null || !projects.length) return;
    let alive = true;
    void Promise.all(
      projects.map(async (p) => {
        const out: Hit[] = [];
        await api.projectBuilds(p.id).then((d) => {
          for (const b of d.builds) out.push({ kind: "build", label: `${p.name} ${b.label || b.key}`, to: `/projects/${p.id}/builds/${encodeURIComponent(b.key)}` });
        }).catch(() => {});
        await api.projectScenarios(p.id).then((d) => {
          for (const c of d.lists[0]?.scenarios ?? []) out.push({ kind: "case", label: `${c.id} · ${c.name}`, to: `/projects/${p.id}/cases/${encodeURIComponent(c.id)}` });
        }).catch(() => {});
        return out;
      }),
    ).then((all) => alive && setHits(all.flat()));
    return () => {
      alive = false;
    };
  }, [open, hits, projects]);
  return hits ?? [];
}

/**
 * Search, on Ctrl K or the button: products, the pages outside a product,
 * benches and the newest runs, from lists the header already holds. Arrows to
 * move, Enter to go, Escape to close.
 */
export function Search({ projects, benches, runs }: { projects: Project[]; benches: Bench[]; runs: Run[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const close = useCallback(() => {
    setOpen(false);
    setQ("");
    setSel(0);
  }, []);
  useDismiss(open, close, box);
  const navigate = useNavigate();

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, []);

  const deep = useDeepHits(open, projects);
  const all: Hit[] = useMemo(
    () => [
      ...projects.map((p) => ({ kind: "product" as const, label: p.name, to: `/projects/${p.id}` })),
      ...deep,
      ...PAGES.map(([to, key]) => ({ kind: "page" as const, label: t(key), to })),
      ...benches.map((b) => ({ kind: "bench" as const, label: b.name, to: `/benches/${b.id}` })),
      ...runs.map((r) => ({ kind: "run" as const, label: `${r.name} · ${r.bench_name}`, to: `/runs/${r.id}` })),
    ],
    [projects, benches, runs, deep, t],
  );
  const v = q.trim().toLowerCase();
  const hits = v ? all.filter((h) => h.label.toLowerCase().includes(v)).slice(0, 10) : all.slice(0, 8);
  const go = (h: Hit) => {
    close();
    navigate(h.to);
  };

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm text-sky-100 hover:bg-brand-600 dark:hover:bg-brand-800"
      >
        <span>{t("product.search")}</span>
        <kbd className="hidden rounded border border-white/30 px-1 font-mono text-xs text-sky-200 sm:inline">Ctrl K</kbd>
      </button>
      {open && (
        <div
          role="dialog"
          aria-label={t("product.search")}
          className="absolute right-0 z-30 mt-2 w-[min(34rem,calc(100vw-2rem))] rounded-2xl border border-slate-200 bg-white p-3 text-slate-900 shadow-2xl dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        >
          <input
            autoFocus
            value={q}
            aria-label={t("product.search")}
            placeholder={t("product.searchPlaceholder")}
            onChange={(e) => {
              setQ(e.target.value);
              setSel(0);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setSel((s) => Math.min(s + 1, hits.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setSel((s) => Math.max(s - 1, 0));
              } else if (e.key === "Enter" && hits[sel]) {
                go(hits[sel]);
              }
            }}
            className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-base dark:border-slate-700 dark:bg-slate-800"
          />
          <ul className="mt-2 space-y-0.5" role="listbox" aria-label={t("product.search")}>
            {hits.map((h, i) => (
              <li key={`${h.kind}${h.to}`} role="option" aria-selected={i === sel}>
                <button
                  type="button"
                  onClick={() => go(h)}
                  onMouseEnter={() => setSel(i)}
                  className={`flex w-full items-baseline gap-3 rounded-lg px-3 py-2 text-left text-sm ${
                    i === sel ? "bg-slate-100 dark:bg-slate-800" : ""
                  }`}
                >
                  <span className="w-16 shrink-0 text-xs text-slate-500">
                    {t(`product.kind.${h.kind}` as "product.kind.run")}
                  </span>
                  <span className="min-w-0 truncate">{h.label}</span>
                </button>
              </li>
            ))}
            {!hits.length && <li className="px-3 py-2 text-sm text-slate-500">{t("product.noHits")}</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Pages outside a product, in the order the More menu lists them. The second
 *  member is a catalogue key, resolved at render - see App.tsx on why. */
export const PAGES: [string, PageKey][] = [
  ["/now", "product.all"],
  ["/runs", "nav.runs"],
  ["/campaigns", "nav.campaigns"],
  ["/reports", "nav.reports"],
  ["/quality", "nav.quality"],
  ["/manual", "nav.manual"],
  ["/people", "nav.people"],
];
export type PageKey = "product.all" | "nav.runs" | "nav.campaigns" | "nav.reports" | "nav.quality" | "nav.manual" | "nav.people";
